// Sidebar — the "On This Page" anchor index used on every guide.
//
// Highlights the currently-visible section as the user scrolls, using an
// IntersectionObserver.

import { useEffect, useState } from 'react';
import type { SidebarSection } from '../types';

interface SidebarProps {
  /** Ordered list of sections to show. Each `id` must match a heading id. */
  sections: SidebarSection[];
}

export default function Sidebar({ sections }: SidebarProps) {
  // ID of the section currently in (or closest to) the viewport.
  // Initialised to the first section so something is highlighted on first
  // paint, before any scroll event has fired.
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');

  useEffect(() => {
    if (sections.length === 0) return;

    // Observe each <h2> with a top margin so sections become "active" a
    // bit before they hit the very top — the navbar takes up some space.
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry currently intersecting with the highest position.
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Pick the one nearest the top of the viewport.
          const top = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveId(top.target.id);
        }
      },
      {
        // Trigger when the top of a section crosses 25% down from the
        // top of the viewport. Tweak this if the active highlighting
        // feels off relative to the visible content.
        rootMargin: '0px 0px -75% 0px',
        threshold: 0,
      }
    );

    // Observe each section's <h2> (or whichever element has that id).
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  return (
    <aside className="sidebar">
      <h4>On This Page</h4>
      <ul>
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className={s.id === activeId ? 'active' : ''}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}
